import { CONTENT_WIDTH } from '../../utils/pdfConstants';
import { getPeitepaksuusByTarkastuskohdeV2 } from '../../../api/api';

// v2 counterpart of renderPeitepaksuusBlock.js. buildTable/buildScatterSvg
// and their helpers below are a deliberate copy (not imported) — v1's PDF
// output stays untouched. v1 loops one table+chart per kohde across a whole
// mittausProjekti; v2's live editor is per-rakenneosa (1:1 with a
// peitepaksuus row), so this renderer fetches every rakenneosa under the
// block's tarkastuskohde (listPeitepaksuusByTarkastuskohde, backend-side)
// to reproduce that same "one table+chart per structure" scope.

const GREEN = '#04aa00';
const YEARS_AHEAD = [10, 20, 30, 40, 50];

const RANGES = [
  { label: '< 6',   test: v => v < 6 },
  { label: '6–10',  test: v => v >= 6  && v <= 10 },
  { label: '11–15', test: v => v >= 11 && v <= 15 },
  { label: '16–20', test: v => v >= 16 && v <= 20 },
  { label: '21–25', test: v => v >= 21 && v <= 25 },
  { label: '26–30', test: v => v >= 26 && v <= 30 },
  { label: '31–35', test: v => v >= 31 && v <= 35 },
  { label: '36–40', test: v => v >= 36 && v <= 40 },
  { label: '41–45', test: v => v >= 41 && v <= 45 },
  { label: '> 45',  test: v => v > 45 },
];

function calcStats(arr) {
  if (!arr.length) return { ka: null, min: null, max: null, khaj: null };
  const min = Math.min(...arr);
  const max = Math.max(...arr);
  const ka = arr.reduce((a, b) => a + b, 0) / arr.length;
  const khaj = arr.length > 1
    ? Math.sqrt(arr.reduce((s, v) => s + (v - ka) ** 2, 0) / (arr.length - 1))
    : 0;
  return { ka, min, max, khaj };
}

function calcDist(arr) {
  const total = arr.length;
  return RANGES.map(r => {
    const count = arr.filter(r.test).length;
    return { count, pct: total ? count / total : 0 };
  });
}

function fmtNum(v, d = 1) {
  if (v === null || v === undefined) return '—';
  return typeof v === 'number' ? (Number.isInteger(v) ? String(v) : v.toFixed(d)) : String(v);
}

function fmtPct(v) {
  if (!v) return '';
  return (v * 100).toFixed(1) + ' %';
}

const F = { font: 'Lato', fontSize: 8 };
const E = { text: '', ...F };

const tCell = (text, opts = {}) => ({ text: String(text ?? ''), ...F, ...opts });
const hCell = (text, opts = {}) => tCell(text, { bold: true, color: 'white', fillColor: GREEN, alignment: 'center', margin: [0, 2, 0, 2], ...opts });
const lCell = (text, opts = {}) => tCell(text, { fillColor: '#f8f9fa', alignment: 'center', fontSize: 7, ...opts });
const rCell = (text, opts = {}) => tCell(text, { alignment: 'center', fontSize: 7, ...opts });

const PAD = 2; // paddingLeft = paddingRight per cell — must match tableLayout below
const NUM_COLS = 12;

const tableLayout = {
  hLineWidth: () => 0.3,
  vLineWidth: () => 0.3,
  hLineColor: () => '#adb5bd',
  vLineColor: () => '#adb5bd',
  paddingLeft: () => PAD,
  paddingRight: () => PAD,
  paddingTop: () => 3,
  paddingBottom: () => 3,
};

function buildScatterSvg(coverArr, carbonStats) {
  const W = 500, H = 160;
  const ml = 38, mr = 100, mt = 8, mb = 28;
  const cW = W - ml - mr;
  const cH = H - mt - mb;

  const safeArr = coverArr.filter(v => Number.isFinite(v) && v >= 0 && v <= 9999);
  const carbMax = Number.isFinite(carbonStats.max) ? carbonStats.max : 0;
  const rawMax = Math.max(...(safeArr.length ? safeArr : [0]), Math.min(carbMax, 9999));
  const maxY = Math.min(Math.ceil(rawMax / 10) * 10 + 10, 1000);

  const xs = i => ml + (safeArr.length > 1 ? (i / (safeArr.length - 1)) * cW : cW / 2);
  const ys = v => mt + cH - (v / maxY) * cH;

  const yTicks = Array.from({ length: Math.floor(maxY / 10) + 1 }, (_, i) => i * 10);
  const step = Math.max(1, Math.ceil(safeArr.length / 6));
  const xTicks = [...Array.from({ length: Math.floor((safeArr.length - 1) / step) + 1 }, (_, i) => i * step), safeArr.length - 1]
    .filter((v, i, a) => a.indexOf(v) === i && v < safeArr.length)
    .sort((a, b) => a - b);

  let s = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}">`;

  yTicks.forEach(v => {
    s += `<line x1="${ml}" y1="${ys(v).toFixed(1)}" x2="${ml + cW}" y2="${ys(v).toFixed(1)}" stroke="#e0e0e0" stroke-width="0.5"/>`;
  });
  s += `<line x1="${ml}" y1="${mt}" x2="${ml}" y2="${mt + cH}" stroke="#666" stroke-width="1"/>`;
  s += `<line x1="${ml}" y1="${mt + cH}" x2="${ml + cW}" y2="${mt + cH}" stroke="#666" stroke-width="1"/>`;
  yTicks.forEach(v => {
    s += `<line x1="${ml - 3}" y1="${ys(v).toFixed(1)}" x2="${ml}" y2="${ys(v).toFixed(1)}" stroke="#666" stroke-width="1"/>`;
    s += `<text x="${ml - 5}" y="${ys(v).toFixed(1)}" text-anchor="end" dominant-baseline="middle" font-size="7" fill="#555">${v}</text>`;
  });
  s += `<text transform="translate(10,${(mt + cH / 2).toFixed(1)}) rotate(-90)" text-anchor="middle" font-size="7" fill="#555">Etaisyys [mm]</text>`;
  xTicks.forEach(i => {
    s += `<line x1="${xs(i).toFixed(1)}" y1="${mt + cH}" x2="${xs(i).toFixed(1)}" y2="${mt + cH + 3}" stroke="#666" stroke-width="1"/>`;
    s += `<text x="${xs(i).toFixed(1)}" y="${mt + cH + 10}" text-anchor="middle" font-size="7" fill="#555">${i + 1}</text>`;
  });
  s += `<text x="${(ml + cW / 2).toFixed(1)}" y="${H - 2}" text-anchor="middle" font-size="7" fill="#555">Mittausten lukumaara [kpl]</text>`;

  if (carbonStats.max !== null)
    s += `<line x1="${ml}" y1="${ys(carbonStats.max).toFixed(1)}" x2="${ml + cW}" y2="${ys(carbonStats.max).toFixed(1)}" stroke="#e74c3c" stroke-width="1.5"/>`;
  if (carbonStats.ka !== null)
    s += `<line x1="${ml}" y1="${ys(carbonStats.ka).toFixed(1)}" x2="${ml + cW}" y2="${ys(carbonStats.ka).toFixed(1)}" stroke="#2980b9" stroke-width="1.5" stroke-dasharray="6 3"/>`;
  if (carbonStats.min !== null)
    s += `<line x1="${ml}" y1="${ys(carbonStats.min).toFixed(1)}" x2="${ml + cW}" y2="${ys(carbonStats.min).toFixed(1)}" stroke="#27ae60" stroke-width="1.5" stroke-dasharray="2 3"/>`;

  safeArr.forEach((v, i) => {
    s += `<circle cx="${xs(i).toFixed(1)}" cy="${ys(v).toFixed(1)}" r="2.5" fill="#888" fill-opacity="0.85"/>`;
  });

  const lx = ml + cW + 10, ly = mt + 4;
  s += `<circle cx="${lx + 5}" cy="${ly + 6}" r="3" fill="#888"/>`;
  s += `<text x="${lx + 12}" y="${ly + 10}" font-size="7" fill="#333">Peitepaksuus</text>`;
  if (carbonStats.max !== null) {
    s += `<line x1="${lx}" y1="${ly + 22}" x2="${lx + 10}" y2="${ly + 22}" stroke="#e74c3c" stroke-width="1.5"/>`;
    s += `<text x="${lx + 12}" y="${ly + 26}" font-size="7" fill="#333">Karb. max</text>`;
  }
  if (carbonStats.ka !== null) {
    s += `<line x1="${lx}" y1="${ly + 38}" x2="${lx + 10}" y2="${ly + 38}" stroke="#2980b9" stroke-width="1.5" stroke-dasharray="4 2"/>`;
    s += `<text x="${lx + 12}" y="${ly + 42}" font-size="7" fill="#333">Karb. ka</text>`;
  }
  if (carbonStats.min !== null) {
    s += `<line x1="${lx}" y1="${ly + 54}" x2="${lx + 10}" y2="${ly + 54}" stroke="#27ae60" stroke-width="1.5" stroke-dasharray="2 2"/>`;
    s += `<text x="${lx + 12}" y="${ly + 58}" font-size="7" fill="#333">Karb. min</text>`;
  }
  s += '</svg>';
  return s;
}

function buildTable(coverArr, carbonArr, age, contentWidth = CONTENT_WIDTH) {
  const cStats = calcStats(coverArr);
  const carbStats = calcStats(carbonArr);
  const cDist = calcDist(coverArr);
  const carbDist = calcDist(carbonArr);

  const corrodedD = carbStats.ka ? (() => {
    const corr = coverArr.filter(v => v <= carbStats.ka);
    return calcDist(corr).map(b => ({ ...b, pct: coverArr.length ? b.count / coverArr.length : 0 }));
  })() : null;
  const corrodedT = corrodedD ? corrodedD.reduce((s, b) => s + b.count, 0) : null;

  const coeff = carbStats.ka && age > 0 ? carbStats.ka / Math.sqrt(age) : null;
  const prog = coeff && age ? YEARS_AHEAD.map(y => coeff * Math.sqrt(age + y)) : null;

  // pdfmake adds paddingLeft+paddingRight ON TOP of each column's specified width,
  // so we must subtract total padding overhead before dividing remaining space.
  const labelW = 148;
  const yhtW = 38;
  const rangeW = (contentWidth - labelW - yhtW - NUM_COLS * PAD * 2) / (10 * 1.02);
  const widths = [labelW, ...Array(10).fill(rangeW), yhtW];

  const spacer = [{ text: '', colSpan: 12, border: [false, false, false, false], margin: [0, 1, 0, 1] }, ...Array(11).fill(E)];

  const body = [
    [hCell('Syvyysalue (mm)'), ...RANGES.map(r => hCell(r.label, { fontSize: 7 })), hCell('YHT')],

    [lCell('Osuus peitepaksuushavainnoista'),
      ...cDist.map(b => rCell(b.pct > 0 ? fmtPct(b.pct) : '')),
      rCell(coverArr.length ? '100.0 %' : '—', { bold: true })],

    [lCell('Osuus karbonatisoitumissyvyyshavainnoista'),
      ...carbDist.map(b => rCell(b.pct > 0 ? fmtPct(b.pct) : '')),
      rCell(carbonArr.length ? '100.0 %' : '—', { bold: true })],

    [lCell('Korroosiotilassa oleva raudoitus'),
      ...(corrodedD
        ? corrodedD.map(b => rCell(b.pct > 0 ? fmtPct(b.pct) : ''))
        : RANGES.map(() => rCell('—', { color: '#adb5bd' }))),
      rCell(corrodedD ? fmtPct(coverArr.length ? corrodedT / coverArr.length : 0) : '—',
        { bold: true, color: corrodedT ? '#c0392b' : undefined })],

    spacer,

    [hCell('Betonipeitemittaukset'),
      hCell('KESKIARVO', { colSpan: 2 }), E,
      rCell(fmtNum(cStats.ka), { bold: true, colSpan: 2 }), E,
      hCell('MIN'), rCell(fmtNum(cStats.min, 0), { bold: true }),
      hCell('MAX'), rCell(fmtNum(cStats.max, 0), { bold: true }),
      hCell('KHAJ'), rCell(fmtNum(cStats.khaj), { bold: true, colSpan: 2 }), E],

    [hCell('Karbonatisoitumismittaukset'),
      hCell('KESKIARVO', { colSpan: 2 }), E,
      rCell(fmtNum(carbStats.ka), { bold: true, colSpan: 2 }), E,
      hCell('MIN'), rCell(fmtNum(carbStats.min, 0), { bold: true }),
      hCell('MAX'), rCell(fmtNum(carbStats.max, 0), { bold: true }),
      hCell('KHAJ'), rCell(fmtNum(carbStats.khaj), { bold: true, colSpan: 2 }), E],

    spacer,

    [hCell('Tutkimusajankohdasta'),
      ...YEARS_AHEAD.map(y => hCell(`${y} v`)),
      hCell('Rakenteen\nikä (a)', { colSpan: 2, fontSize: 7 }), E,
      tCell(age ? String(age) : '—', { alignment: 'center', bold: true, margin: [0, 5, 0, 0] }),
      hCell('Karb.\nkerroin', { colSpan: 2, fontSize: 7 }), E,
      tCell(coeff ? coeff.toFixed(1) : '—', { alignment: 'center', bold: true, margin: [0, 5, 0, 0] })],

    [lCell('Karbonatisoitumisen eteneminen (mm)'),
      ...(prog
        ? prog.map(v => rCell(v.toFixed(1), { bold: true }))
        : YEARS_AHEAD.map(() => rCell('—', { color: '#adb5bd' }))),
      { ...tCell(coeff ? `k = ${fmtNum(carbStats.ka)} / sqrt(${age}) = ${coeff.toFixed(4)}, eteneminen = k * sqrt(ika + v)` : ''),
        fontSize: 7, color: '#6c757d', colSpan: 6 },
      ...Array(5).fill(E)],
  ];

  return { widths, body };
}

export async function renderPeitepaksuusBlockV2({ block, content, tableCounter, contentWidth = CONTENT_WIDTH }) {
  const nav = block?.data?.nav ?? {};
  if (!nav.tkId) return;

  let entries;
  try { entries = (await getPeitepaksuusByTarkastuskohdeV2(nav.tkId)).data ?? []; }
  catch (e) { console.warn('[RenderPeitepaksuusBlockV2] failed to fetch peitepaksuus:', e); return; }
  if (!entries.length) return;

  entries.forEach((entry, idx) => {
    const coverArr = (entry.cover_measurements ?? []).map(m => m.arvo_mm).filter(v => v > 0);
    const carbonArr = [...(entry.karbonatisaatio?.ylapinta ?? []), ...(entry.karbonatisaatio?.alapinta ?? [])];
    const age = entry.age ?? null;
    const tblNum = tableCounter ? ++tableCounter.value : (idx + 1);

    const { widths, body } = buildTable(coverArr, carbonArr, age, contentWidth);

    // Rich-text captions (TaulukkoFooter/MinimalLexical) are persisted as
    // JSON.stringify({value, html}) in the TEXT column — parse it back to
    // plain text for the PDF; a bare string (pre-rich-text data) passes
    // through unchanged.
    const readCap = (raw) => {
      if (!raw) return '';
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object' && 'value' in parsed) return (parsed.value ?? '').trim();
      } catch { /* plain string, from before rich-text captions */ }
      return String(raw).trim();
    };
    // No fallback — an empty caption stays empty in the PDF. The table
    // number still always shows.
    const tableCaption = readCap(entry.caption);
    const chartCaption = readCap(entry.chart_caption);

    content.push({
      stack: [
        { table: { widths, body }, layout: tableLayout, margin: [0, 5, 0, 0] },
        {
          text: tableCaption
            ? [{ text: `Taulukko ${tblNum}. `, bold: true }, { text: tableCaption, bold: false }]
            : [{ text: `Taulukko ${tblNum}.`, bold: true }],
          font: 'Lato', fontSize: 9, margin: [0, 10, 0, 0],
        },
      ],
      margin: [0, 0, 0, 15],
    });

    if (coverArr.length > 0) {
      const carbStats = calcStats(carbonArr);
      const chartNum = tableCounter ? ++tableCounter.value : tblNum + 1;
      content.push({
        stack: [
          { svg: buildScatterSvg(coverArr, carbStats), width: contentWidth - 24, margin: [24, 0, 0, 0] },
          {
            text: chartCaption
              ? [{ text: `Taulukko ${chartNum}. `, bold: true }, { text: chartCaption, bold: false }]
              : [{ text: `Taulukko ${chartNum}.`, bold: true }],
            font: 'Lato', fontSize: 9,
            margin: [0, 10, 0, 0],
          },
        ],
        margin: [0, 0, 0, 15],
      });
    }
  });
}
